import React, {
  type ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  isTrackReference, useLocalParticipant, useParticipants, useTracks,
} from '@livekit/components-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import {
  ParticipantEvent, Track, type LocalParticipant, type Participant,
} from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff } from 'lucide-react-native';
import {
  Box, Center, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';

import type { MeetingRoomRole } from '@/common/components/meeting/types';
import { ParticipantTile } from '@/common/components/meeting/MeetingVideoTiles';
import { BriefingHandIndicator } from '@/common/components/oralExam/BriefingParticipantControls';
import log from '@/common/utils/logger';

export const ORAL_EXAM_BRIEFING_TOPIC = 'oral-exam-briefing';
export const HAND_RAISED_ATTRIBUTE = 'handRaised';

export type SpeakPermissionPayload = {
  type: 'oralExam.speakPermission';
  targetIdentity: string;
  allowed: boolean;
};

export type BriefingDepartureMessageType = 'starting' | 'started' | 'cancelled';

export type BriefingDeparturePayload = {
  type: BriefingDepartureMessageType;
};

/** Encode a briefing departure signal for the shared oral-exam-briefing topic. */
export function encodeBriefingDeparturePayload(type: BriefingDepartureMessageType): Uint8Array {
  return new TextEncoder().encode(JSON.stringify({ type } satisfies BriefingDeparturePayload));
}

/** Parse departure payloads; returns null for speak-permission or malformed packets. */
export function parseBriefingDeparturePayload(payload: Uint8Array | ArrayBuffer): BriefingDeparturePayload | null {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(payload)) as Partial<BriefingDeparturePayload>;
    if (parsed?.type === 'starting' || parsed?.type === 'started' || parsed?.type === 'cancelled') {
      return { type: parsed.type };
    }
  } catch {
    /* ignore malformed packets */
  }
  return null;
}

/** Whether the participant's hand is raised according to LiveKit attributes. */
export function isHandRaised(participant: Participant): boolean {
  return participant.attributes?.[HAND_RAISED_ATTRIBUTE] === '1';
}

/** Parse LiveKit participant metadata role (lecturer | student). */
export function parseParticipantRole(participant: Participant): string | undefined {
  try {
    const meta = JSON.parse(participant.metadata ?? '{}') as { role?: string };
    return meta.role;
  } catch {
    return undefined;
  }
}

/**
 * Classify lecturer by the signed identity suffix first.
 * Metadata is client-writable (the hand attribute uses that grant), so it
 * only applies when the identity has no `:lecturer` / `:student` suffix.
 */
export function isLecturerParticipant(participant: Participant): boolean {
  if (!participant?.identity) return false;
  if (participant.identity.endsWith(':lecturer')) return true;
  if (participant.identity.endsWith(':student')) return false;
  return parseParticipantRole(participant) === 'lecturer';
}

export function preflightLecturerIdentity(lecturerId: string, slotId: string): string {
  return `${lecturerId}:slot:${slotId}:lecturer`;
}

export function parsePreflightLecturerId(identity: string, slotId: string): string | null {
  const suffix = `:slot:${slotId}:lecturer`;
  if (identity.endsWith(suffix) && identity.length > suffix.length) {
    return identity.slice(0, -suffix.length);
  }
  return null;
}

function cameraPlaceholderFor(participant: Participant): TrackReferenceOrPlaceholder {
  return { participant, source: Track.Source.Camera };
}

/**
 * Resolve the lecturer camera track (live, muted, or placeholder) from the
 * subscribed track list plus room participants. Falls back to a synthetic
 * placeholder when the lecturer is connected but `useTracks` has not yet
 * surfaced their local publication.
 */
export function resolveLecturerCameraTrack(
  cameraTracks: TrackReferenceOrPlaceholder[],
  participants: Participant[],
  localParticipant: Participant,
  viewerRole?: MeetingRoomRole,
): TrackReferenceOrPlaceholder | undefined {
  if (viewerRole === 'lecturer') {
    const localTrack = cameraTracks.find((trackRef) => trackRef.participant.isLocal);
    return localTrack ?? cameraPlaceholderFor(localParticipant);
  }

  const byRole = cameraTracks.find(
    (trackRef) => isLecturerParticipant(trackRef.participant),
  );
  if (byRole) return byRole;

  const lecturerParticipant = participants.find(isLecturerParticipant);
  if (lecturerParticipant) {
    const byIdentity = cameraTracks.find(
      (trackRef) => trackRef.participant.identity === lecturerParticipant.identity,
    );
    return byIdentity ?? cameraPlaceholderFor(lecturerParticipant);
  }

  if (isLecturerParticipant(localParticipant)) {
    const localTrack = cameraTracks.find((trackRef) => trackRef.participant.isLocal);
    return localTrack ?? cameraPlaceholderFor(localParticipant);
  }

  return undefined;
}

export function resolveBrieferCameraTrack(
  cameraTracks: TrackReferenceOrPlaceholder[],
  participants: Participant[],
  localParticipant: Participant,
  brieferIdentity: string | undefined,
  viewerRole?: MeetingRoomRole,
): TrackReferenceOrPlaceholder | undefined {
  if (!brieferIdentity) {
    return resolveLecturerCameraTrack(
      cameraTracks,
      participants,
      localParticipant,
      viewerRole,
    );
  }

  const brieferParticipant = participants.find((p) => p.identity === brieferIdentity);
  const brieferTrack = cameraTracks.find(
    (trackRef) => trackRef.participant.identity === brieferIdentity,
  );
  if (brieferTrack) return brieferTrack;
  if (brieferParticipant) return cameraPlaceholderFor(brieferParticipant);

  if (localParticipant.identity === brieferIdentity) {
    const localTrack = cameraTracks.find((trackRef) => trackRef.participant.isLocal);
    return localTrack ?? cameraPlaceholderFor(localParticipant);
  }

  return undefined;
}

function useHandRaisedAttribute(participant: Participant): boolean {
  const [raised, setRaised] = useState(() => isHandRaised(participant));

  useEffect(() => {
    const sync = () => setRaised(isHandRaised(participant));
    sync();
    participant.on('attributesChanged', sync);
    return () => {
      participant.off('attributesChanged', sync);
    };
  }, [participant]);

  return raised;
}

type BriefingStudentHandProps = {
  participant: Participant;
  localParticipant: LocalParticipant;
  mode: 'indicator' | 'control';
  onHandError: (error: 'raise' | 'lower' | null) => void;
};

function BriefingStudentHand({
  participant,
  localParticipant,
  mode,
  onHandError,
}: BriefingStudentHandProps) {
  const isOwnTile = participant.identity === localParticipant.identity
    && !isLecturerParticipant(localParticipant);
  const handRaised = useHandRaisedAttribute(participant);

  const handleToggleHand = useCallback(async () => {
    if (!isOwnTile) return;
    const nextRaised = !handRaised;
    onHandError(null);
    try {
      await localParticipant.setAttributes({
        [HAND_RAISED_ATTRIBUTE]: nextRaised ? '1' : '',
      });
    } catch (error) {
      log.warn('Briefing hand update failed', error);
      onHandError(nextRaised ? 'raise' : 'lower');
    }
  }, [handRaised, isOwnTile, localParticipant, onHandError]);

  if (mode === 'control') {
    if (!isOwnTile) return null;
    return (
      <BriefingHandIndicator
        identity={participant.identity}
        raised={handRaised}
        interactive
        onToggle={() => { handleToggleHand(); }}
      />
    );
  }

  if (!handRaised) return null;

  return (
    <Box position="absolute" top="$1" right="$1" zIndex={2} pointerEvents="none">
      <BriefingHandIndicator identity={participant.identity} raised />
    </Box>
  );
}

type BriefingCoSupervisorTileProps = {
  trackRef: TrackReferenceOrPlaceholder;
};

function BriefingCoSupervisorTile({ trackRef }: BriefingCoSupervisorTileProps) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const { isLocal } = trackRef.participant;
  const [micMuted, setMicMuted] = useState(true);
  const [micError, setMicError] = useState<string | null>(null);
  const hadLiveMicRef = useRef(false);
  const desiredEnabledRef = useRef(false);
  const micChainRef = useRef(Promise.resolve());
  const initialMuteStartedRef = useRef(false);
  const micFailedCopy = t('oralExam.lecturer.preflightReview.supervisors.micFailed', {
    defaultValue: 'Microphone unavailable. Try again.',
  });

  const syncMicFromPublication = useCallback(() => {
    const publication = localParticipant.getTrackPublication(Track.Source.Microphone);
    const live = publication?.track != null && !publication.isMuted;
    if (desiredEnabledRef.current) {
      if (live) {
        hadLiveMicRef.current = true;
        setMicMuted(false);
        setMicError(null);
        return;
      }
      // A mute event while the lecturer wants the mic on is the in-flight
      // default mute finishing. Only a vanished track is a real loss.
      if (hadLiveMicRef.current && (publication == null || publication.track == null)) {
        setMicMuted(true);
        setMicError(micFailedCopy);
      }
      return;
    }
    setMicMuted(true);
    if (hadLiveMicRef.current && (publication == null || publication.track == null)) {
      setMicError(micFailedCopy);
    }
  }, [localParticipant, micFailedCopy]);

  const applyMicEnabled = useCallback((enabled: boolean) => {
    desiredEnabledRef.current = enabled;
    const run = micChainRef.current.then(async () => {
      if (desiredEnabledRef.current !== enabled) return;
      try {
        await localParticipant.setMicrophoneEnabled(enabled);
        if (desiredEnabledRef.current !== enabled) return;
        setMicError(null);
        setMicMuted(!enabled);
        if (enabled) hadLiveMicRef.current = true;
      } catch {
        if (desiredEnabledRef.current !== enabled) return;
        setMicMuted(true);
        if (enabled) setMicError(micFailedCopy);
      }
    });
    micChainRef.current = run.then(() => undefined, () => undefined);
    return run;
  }, [localParticipant, micFailedCopy]);

  useEffect(() => {
    if (!isLocal || initialMuteStartedRef.current) return;
    initialMuteStartedRef.current = true;
    applyMicEnabled(false);
  }, [applyMicEnabled, isLocal]);

  useEffect(() => {
    if (!isLocal) return undefined;
    const onTrackLifecycle = () => {
      syncMicFromPublication();
    };
    localParticipant.on(ParticipantEvent.LocalTrackPublished, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.LocalTrackUnpublished, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.TrackMuted, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.TrackUnpublished, onTrackLifecycle);
    return () => {
      localParticipant.off(ParticipantEvent.LocalTrackPublished, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.LocalTrackUnpublished, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.TrackMuted, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.TrackUnpublished, onTrackLifecycle);
    };
  }, [isLocal, localParticipant, syncMicFromPublication]);

  const handleToggleMic = useCallback(() => {
    if (!isLocal) return undefined;
    const nextEnabled = micMuted || micError != null;
    return applyMicEnabled(nextEnabled);
  }, [applyMicEnabled, isLocal, micError, micMuted]);

  const { identity } = trackRef.participant;

  return (
    <Box testID={`briefing-co-supervisor-tile-${identity}`}>
      <Box height={120} borderRadius="$md" overflow="hidden" position="relative">
        <ParticipantTile trackRef={trackRef} rounded="md" showNameBadge={false} />
        {!isLocal && !trackRef.participant.isMicrophoneEnabled ? (
          <Box position="absolute" bottom="$1" left="$1" pointerEvents="none">
            <Icon as={MicOff} size="sm" color="$statusError" testID={`briefing-co-supervisor-mic-off-${identity}`} />
          </Box>
        ) : null}
      </Box>
      {isLocal ? (
        <HStack space="xs" alignItems="center" mt="$1">
          <Pressable
            onPress={() => {
              handleToggleMic()?.catch(() => undefined);
            }}
            accessibilityRole="button"
            testID={`briefing-co-supervisor-mic-toggle-${identity}`}
            minWidth={36}
            minHeight={36}
            alignItems="center"
            justifyContent="center"
          >
            {micMuted || micError ? (
              <Icon
                as={MicOff}
                size="sm"
                color="$statusError"
                testID={`briefing-co-supervisor-mic-off-local-${identity}`}
              />
            ) : (
              <Icon
                as={Mic}
                size="sm"
                color="$textBody"
                testID={`briefing-co-supervisor-mic-on-${identity}`}
              />
            )}
          </Pressable>
          {micError ? (
            <Text size="xs" color="$statusError" flex={1} testID="briefing-co-supervisor-mic-error">
              {micError}
            </Text>
          ) : null}
        </HStack>
      ) : (
        <Text size="xs" color="$textBody" numberOfLines={2} mt="$1">
          {trackRef.participant.name || identity}
        </Text>
      )}
    </Box>
  );
}

export type BriefingConferenceStageProps = {
  /** Optional status overlay rendered on each student thumbnail (e.g. muted mic). */
  renderTileOverlay?: (participant: Participant) => ReactNode;
  /** Optional action rendered below each student thumbnail (e.g. Mute/Unmute). */
  renderTileControl?: (participant: Participant) => ReactNode;
  /** When the local viewer is the lecturer, always surface them in the big tile. */
  viewerRole?: MeetingRoomRole;
  slotId?: string;
  brieferId?: string | null;
  slotOwnerId?: string | null;
};

/**
 * Group-briefing conference layout: briefer camera large, co-supervisors in a
 * column on the right, student cameras in a horizontal strip below.
 */
export function BriefingConferenceStage({
  renderTileOverlay,
  renderTileControl,
  viewerRole,
  slotId,
  brieferId,
  slotOwnerId,
}: BriefingConferenceStageProps) {
  const { t } = useTranslation();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const [handError, setHandError] = useState<'raise' | 'lower' | null>(null);
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
  );

  const effectiveBrieferLecturerId = brieferId ?? slotOwnerId ?? undefined;
  const brieferIdentity = useMemo(() => (
    slotId && effectiveBrieferLecturerId
      ? preflightLecturerIdentity(effectiveBrieferLecturerId, slotId)
      : undefined
  ), [effectiveBrieferLecturerId, slotId]);

  const brieferTrack = useMemo(
    () => resolveBrieferCameraTrack(
      cameraTracks,
      participants,
      localParticipant,
      brieferIdentity,
      viewerRole,
    ),
    [brieferIdentity, cameraTracks, localParticipant, participants, viewerRole],
  );

  const coSupervisorTracks = useMemo(() => {
    if (!brieferIdentity) return [];
    const matches = cameraTracks.filter((trackRef) => {
      if (!isLecturerParticipant(trackRef.participant)) return false;
      return trackRef.participant.identity !== brieferIdentity;
    });
    // withPlaceholder can emit a placeholder beside the live camera for the
    // same person. One card per lecturer.
    const byIdentity = new Map<string, TrackReferenceOrPlaceholder>();
    for (const trackRef of matches) {
      const { identity } = trackRef.participant;
      const current = byIdentity.get(identity);
      const currentHasMedia = current != null
        && isTrackReference(current)
        && Boolean(current.publication.track);
      const nextHasMedia = isTrackReference(trackRef) && Boolean(trackRef.publication.track);
      if (!current || (!currentHasMedia && nextHasMedia)) {
        byIdentity.set(identity, trackRef);
      }
    }
    return [...byIdentity.values()];
  }, [brieferIdentity, cameraTracks]);

  const studentTracks = useMemo(
    () => cameraTracks.filter(
      (trackRef) => !isLecturerParticipant(trackRef.participant),
    ),
    [cameraTracks],
  );

  const brieferWasConnectedRef = useRef(false);
  const brieferDisplayNameRef = useRef<string | null>(null);

  const brieferParticipant = useMemo(() => {
    if (!brieferIdentity) return participants.find(isLecturerParticipant);
    return participants.find((p) => p.identity === brieferIdentity);
  }, [brieferIdentity, participants]);

  if (brieferParticipant) {
    brieferWasConnectedRef.current = true;
    brieferDisplayNameRef.current = brieferParticipant.name || brieferParticipant.identity;
  }

  const brieferConnectionDropped = Boolean(
    brieferIdentity
    && brieferWasConnectedRef.current
    && !brieferParticipant,
  );

  const hasLecturerInRoom = useMemo(
    () => viewerRole === 'lecturer'
      || participants.some(isLecturerParticipant)
      || isLecturerParticipant(localParticipant),
    [localParticipant, participants, viewerRole],
  );

  const isLocalStudent = !isLecturerParticipant(localParticipant);

  return (
    <HStack flex={1} minHeight={180} space="sm" testID="briefing-conference-stage">
      <VStack flex={1} minHeight={180} space="sm">
        <Box
          width="100%"
          flex={1}
          minHeight={180}
          borderRadius="$lg"
          overflow="hidden"
          borderWidth={1}
          borderColor="$borderLight"
          bg="$bgSubtle"
          position="relative"
          testID="briefing-lecturer-tile"
        >
          {brieferConnectionDropped ? (
            <Center flex={1} bg="$bgSubtle" px="$4" testID="briefing-briefer-dropped">
              <Text size="sm" color="$textHeading" fontWeight="semibold" textAlign="center">
                {`${brieferDisplayNameRef.current ?? t('oralExam.groupBriefing.brieferFallbackName', {
                  defaultValue: 'The supervisor',
                })}${t('oralExam.groupBriefing.brieferConnectionDroppedSuffix', {
                  defaultValue: "'s connection dropped",
                })}`}
              </Text>
            </Center>
          ) : brieferTrack ? (
            <Box position="absolute" top={0} left={0} right={0} bottom={0}>
              <ParticipantTile trackRef={brieferTrack} rounded="lg" initialsSize="4xl" />
            </Box>
          ) : hasLecturerInRoom ? (
            <Center flex={1} bg="$bgDark" testID="briefing-lecturer-connecting" />
          ) : (
            <Center flex={1} bg="$bgSubtle" px="$4" testID="briefing-waiting-for-lecturer">
              <VStack space="xs" alignItems="center">
                <Text size="sm" color="$textHeading" fontWeight="semibold" textAlign="center">
                  {t('oralExam.groupBriefing.waitingForVideo', {
                    defaultValue: "Waiting for the lecturer's video",
                  })}
                </Text>
                <Text size="sm" color="$textSubtle" textAlign="center">
                  {t('oralExam.groupBriefing.waitingHint', {
                    defaultValue: 'Stay here. The briefing will begin shortly.',
                  })}
                </Text>
              </VStack>
            </Center>
          )}
        </Box>

        {studentTracks.length > 0 ? (
          <HStack height={174} space="sm" flexShrink={0} testID="briefing-student-strip">
            {studentTracks.map((trackRef) => {
              const { identity } = trackRef.participant;
              const displayName = trackRef.participant.name || identity;
              const isOwnTile = identity === localParticipant.identity && isLocalStudent;
              return (
                <VStack
                  key={`${identity}-${trackRef.source}`}
                  width={150}
                  flexShrink={0}
                  space="xs"
                >
                  <Box
                    flex={1}
                    position="relative"
                    testID={`briefing-student-tile-${identity}`}
                  >
                    <ParticipantTile
                      trackRef={trackRef}
                      rounded="md"
                      showNameBadge={false}
                    />
                    <BriefingStudentHand
                      participant={trackRef.participant}
                      localParticipant={localParticipant}
                      mode="indicator"
                      onHandError={setHandError}
                    />
                    {renderTileOverlay ? (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        pointerEvents="box-none"
                      >
                        {renderTileOverlay(trackRef.participant)}
                      </Box>
                    ) : null}
                  </Box>
                  <Text
                    size="xs"
                    color="$textBody"
                    numberOfLines={2}
                    testID={`briefing-student-name-${identity}`}
                  >
                    {displayName}
                  </Text>
                  <BriefingStudentHand
                    participant={trackRef.participant}
                    localParticipant={localParticipant}
                    mode="control"
                    onHandError={setHandError}
                  />
                  {renderTileControl ? renderTileControl(trackRef.participant) : null}
                  {isOwnTile && !!handError ? (
                    <Text
                      size="xs"
                      color="$statusError"
                      testID="briefing-raise-hand-error"
                    >
                      {handError === 'raise'
                        ? t('oralExam.groupBriefing.raiseHandFailed', {
                          defaultValue: "Couldn't raise your hand. Tap again.",
                        })
                        : t('oralExam.groupBriefing.lowerHandFailed', {
                          defaultValue: "Couldn't lower your hand. Tap again.",
                        })}
                    </Text>
                  ) : null}
                </VStack>
              );
            })}
          </HStack>
        ) : null}
      </VStack>

      {coSupervisorTracks.length > 0 ? (
        <VStack
          width={150}
          flexShrink={0}
          space="sm"
          testID="briefing-co-supervisors-column"
        >
          {coSupervisorTracks.map((trackRef) => (
            <BriefingCoSupervisorTile
              key={trackRef.participant.identity}
              trackRef={trackRef}
            />
          ))}
        </VStack>
      ) : null}
    </HStack>
  );
}
